import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { TicketAlert } from "@/components/helpdesk/TicketAlert";
import { OnLeaveTodayCard } from "@/components/school/OnLeaveTodayCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, GraduationCap, BookOpen, Presentation, ClipboardCheck, Award,
  AlertTriangle, Trophy, TrendingUp, Calendar, ArrowRight, UserSquare,
  LineChart, History, Sparkles,
} from "lucide-react";
import { YEAR_LABEL, TRACK_SHORT } from "@/lib/school";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { canAccess } from "@/lib/permissions";

const SCHOOL_TILES = [
  { itemKey: "piano",         to: "/scuola/piano",            label: "Calendario",    icon: Calendar,       color: "#1C352D", testId: "school-tile-piano" },
  { itemKey: "students",      to: "/scuola/studenti",         label: "Studenti",      icon: GraduationCap,  color: "#2F5D50", testId: "school-tile-students" },
  { itemKey: "teachers",      to: "/scuola/docenti",          label: "Docenti",       icon: UserSquare,     color: "#4A6572", testId: "school-tile-teachers" },
  { itemKey: "courses",       to: "/scuola/corsi",            label: "Corsi",         icon: BookOpen,       color: "#556B2F", testId: "school-tile-courses" },
  { itemKey: "seminars",      to: "/scuola/seminari",         label: "Seminari",      icon: Presentation,   color: "#6B4E71", testId: "school-tile-seminars" },
  { itemKey: "attendance",    to: "/scuola/presenze",         label: "Presenze",      icon: ClipboardCheck, color: "#B4632F", testId: "school-tile-attendance" },
  { itemKey: "exams",         to: "/scuola/esami",            label: "Esami",         icon: Award,          color: "#E2725B", testId: "school-tile-exams" },
  { itemKey: "storico_esami", to: "/scuola/storico-esami",    label: "Storico esami", icon: History,        color: "#8A6D4A", testId: "school-tile-storico" },
  { itemKey: "statistics",    to: "/scuola/statistiche",      label: "Statistiche",   icon: LineChart,      color: "#3B5B8C", testId: "school-tile-stats" },
  { itemKey: "practical",     to: "/scuola/crediti-pratici",  label: "Crediti pratici", icon: Sparkles,     color: "#7A5C3F", testId: "school-tile-practical" },
  { itemKey: "certs",         to: "/scuola/attestati",        label: "Attestati",     icon: Award,          color: "#8C4A6B", testId: "school-tile-certs" },
];

function SchoolTile({ to, label, icon: Icon, color, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:border-stone-300"
    >
      <div
        className="w-10 h-10 rounded-lg grid place-items-center text-white transition-transform duration-300 group-hover:scale-110"
        style={{ backgroundColor: color }}
      >
        <Icon size={18} strokeWidth={1.75}/>
      </div>
      <div className="text-[12px] font-medium text-stone-800 text-center leading-tight">{label}</div>
    </Link>
  );
}

const STATUS_COLORS = {
  attivo: "text-emerald-700",
  sospeso: "text-orange-700",
  ritirato: "text-stone-500",
  diplomato: "text-indigo-700",
};

const KIND_LABEL = {
  lezione: "Lezione", riposo: "Riposo", uscita_missionaria: "Uscita",
  viaggio_missionario: "Viaggio", esame: "Esame", pranzo: "Pranzo",
  preghiera: "Preghiera", altro: "Evento",
};

export default function SchoolDashboardV2() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["school-dashboard"],
    queryFn: async () => (await api.get("/school-stats/dashboard")).data,
    refetchInterval: 60_000,
  });

  if (isLoading || !data) {
    return (
      <div className="p-10 text-center text-sm text-stone-400">Caricamento dashboard…</div>
    );
  }

  const { students, faculty, catalog, exams, attendance_30d, cfu, next_events, at_risk, top_performers } = data;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1500px] mx-auto">
      <PageHeader
        title="Dashboard scuola"
        subtitle="Vista strategica su studenti, docenti, presenze, esami e piano didattico."
      />
      <TicketAlert />
      <OnLeaveTodayCard />

      {/* Scorciatoie sottosezioni */}
      <section className="mb-6" data-testid="school-tiles">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-3">Sottosezioni</div>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-11 gap-2.5">
          {SCHOOL_TILES.filter((t) => canAccess(user, "school", t.itemKey)).map((t) => <SchoolTile key={t.to} {...t}/>)}
        </div>
      </section>

      {/* KPI top */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <KpiCard icon={GraduationCap} label="Studenti attivi" value={students.active} sub={`${students.total} tot`} testId="kpi-students-active"/>
        <KpiCard icon={Users} label="Diplomati" value={students.graduated} sub="storico" testId="kpi-students-graduated"/>
        <KpiCard icon={UserSquare} label="Docenti" value={faculty.teachers_active} sub={`${faculty.teachers_total} tot`} testId="kpi-teachers"/>
        <KpiCard icon={BookOpen} label="Corsi" value={catalog.courses} sub={`${catalog.areas} aree`} testId="kpi-courses"/>
        <KpiCard icon={Presentation} label="Seminari" value={catalog.seminars} sub="attivi" testId="kpi-seminars"/>
        <KpiCard icon={Award} label="Voti registrati" value={exams.grades_recorded} sub={`${exams.upcoming_7d} appelli 7g`} testId="kpi-grades"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribuzione studenti */}
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none lg:col-span-2">
          <div className="text-sm font-medium flex items-center gap-2 mb-4"><TrendingUp size={14}/> Studenti attivi per anno e percorso</div>
          <StudentDistribution byYearTrack={students.by_year_track}/>
        </Card>

        {/* Presenze 30gg */}
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-sm font-medium flex items-center gap-2 mb-4"><ClipboardCheck size={14}/> Presenze ultimi 30 giorni</div>
          <div className="text-4xl font-semibold text-[#1C352D]" data-testid="dashboard-attendance-rate">
            {attendance_30d.rate.toFixed(0)}<span className="text-base text-stone-400">%</span>
          </div>
          <div className="text-xs text-stone-500 mt-1">{attendance_30d.total} rilevazioni</div>
          <div className="mt-4 space-y-2">
            <AttendanceBar label="Presente" val={attendance_30d.counts.presente} total={attendance_30d.total} color="#16a34a"/>
            <AttendanceBar label="Ritardo"  val={attendance_30d.counts.ritardo}  total={attendance_30d.total} color="#f59e0b"/>
            <AttendanceBar label="Giustif."  val={attendance_30d.counts.giustificato} total={attendance_30d.total} color="#0891b2"/>
            <AttendanceBar label="Assente"   val={attendance_30d.counts.assente} total={attendance_30d.total} color="#dc2626"/>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Prossimi appelli */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2"><Award size={14}/> Prossimi appelli</div>
            <Link to="/scuola/esami" className="text-xs text-[#E2725B] hover:underline">Tutti</Link>
          </div>
          {exams.next.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 text-center">Nessun appello in programma.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {exams.next.map((s) => (
                <li key={s.id} data-testid={`dashboard-exam-${s.id}`}>
                  <Link to={`/scuola/esami/${s.id}`} className="p-4 flex items-center gap-3 hover:bg-stone-50/60">
                    <div className="w-12 h-12 rounded-lg bg-[#FDFBF7] grid place-items-center border border-stone-200">
                      <div className="text-[10px] uppercase tracking-widest text-stone-500 leading-none">{fmtDate(s.date, {month:"short"}).split(" ")[1] || ""}</div>
                      <div className="text-lg font-semibold text-[#1C352D] leading-none">{parseInt(s.date?.slice(8, 10) || "0", 10)}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{s.course_name || "Corso"}</div>
                      <div className="text-[11px] text-stone-500">{s.time?.slice(0,5) || "—"} · {s.bookings_count} prenotati</div>
                    </div>
                    <Badge className={`text-[10px] rounded-full ${s.status === "aperto" ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-stone-100 text-stone-700"}`}>{s.status}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Prossimi eventi */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2"><Calendar size={14}/> Eventi settimana</div>
            <Link to="/scuola/piano" className="text-xs text-[#E2725B] hover:underline">Calendario</Link>
          </div>
          {next_events.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 text-center">Nessun evento in agenda.</div>
          ) : (
            <ul className="divide-y divide-stone-100 max-h-[380px] overflow-y-auto">
              {next_events.slice(0, 10).map((e) => (
                <li key={e.id} className="p-3 flex items-center gap-3">
                  <div className="w-10 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 leading-none">{fmtDate(e.date, {weekday:"short"}).split(" ")[0]}</div>
                    <div className="text-base font-semibold text-[#1C352D] leading-tight">{parseInt(e.date?.slice(8,10) || "0", 10)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{e.title}</div>
                    <div className="text-[11px] text-stone-500">{e.start_time?.slice(0,5) || "—"} · {KIND_LABEL[e.kind] || e.kind}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Top performers */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2"><Trophy size={14} className="text-amber-500"/> Migliori studenti (media ≥ 27)</div>
          </div>
          {top_performers.length === 0 ? (
            <div className="p-8 text-sm text-stone-400 text-center">Ancora pochi voti registrati per fare una classifica.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {top_performers.map((s, i) => (
                <li key={s.id} data-testid={`dashboard-top-${s.id}`}>
                  <Link to={`/scuola/studenti/${s.id}/statistiche`} className="p-3 flex items-center gap-3 hover:bg-stone-50/60">
                    <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold grid place-items-center">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-[11px] text-stone-500">Matricola {s.matricola} · {YEAR_LABEL[s.year_level] || `${s.year_level}° anno`} · {s.exams} esami</div>
                    </div>
                    <div className="text-lg font-semibold text-[#1C352D]">{s.media.toFixed(2)}</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Studenti a rischio */}
        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 flex items-center justify-between">
            <div className="text-sm font-medium flex items-center gap-2"><AlertTriangle size={14} className="text-red-500"/> Studenti a rischio (presenze &lt; 70%)</div>
          </div>
          {at_risk.length === 0 ? (
            <div className="p-8 text-sm text-emerald-700 text-center">Nessuno studente sotto la soglia. Ottimo!</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {at_risk.map((s) => (
                <li key={s.id} data-testid={`dashboard-atrisk-${s.id}`}>
                  <Link to={`/scuola/studenti/${s.id}/statistiche`} className="p-3 flex items-center gap-3 hover:bg-stone-50/60">
                    <div className="w-7 h-7 rounded-full bg-red-100 text-red-800 grid place-items-center"><AlertTriangle size={13}/></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-[11px] text-stone-500">Matricola {s.matricola} · {YEAR_LABEL[s.year_level] || `${s.year_level}° anno`} · {TRACK_SHORT?.[s.track_type] || s.track_type}</div>
                    </div>
                    <div className="text-lg font-semibold text-red-700">{s.attendance_rate.toFixed(0)}%</div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatusPill label="Attivi" value={students.active} color="emerald"/>
        <StatusPill label="Sospesi" value={students.suspended} color="orange"/>
        <StatusPill label="Diplomati" value={students.graduated} color="indigo"/>
      </div>

      <Card className="mt-6 p-5 border border-stone-200 rounded-xl shadow-none bg-gradient-to-br from-[#1C352D] to-[#264A3E] text-white">
        <div className="text-sm font-medium">CFU medi maturati per studente attivo</div>
        <div className="text-3xl font-semibold mt-1">{cfu.avg_earned_per_student} <span className="text-sm opacity-70">/ {cfu.target} target diploma</span></div>
      </Card>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        <Icon size={11}/> {label}
      </div>
      <div className="text-2xl font-semibold mt-1 text-[#1C352D]">{value}</div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </Card>
  );
}

function StatusPill({ label, value, color }) {
  const colorMap = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    indigo: "bg-indigo-50 text-indigo-800 border-indigo-200",
  };
  return (
    <div className={`p-4 rounded-xl border ${colorMap[color]} flex items-center justify-between`}>
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function AttendanceBar({ label, val, total, color }) {
  const pct = total ? (val / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-stone-600 mb-1">
        <span>{label}</span>
        <span>{val} · {pct.toFixed(0)}%</span>
      </div>
      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }}/>
      </div>
    </div>
  );
}

function StudentDistribution({ byYearTrack }) {
  const years = ["1", "2", "3"];
  const tracks = ["residenziale", "modulare"];
  const trackColor = { residenziale: "#1C352D", modulare: "#E2725B" };
  const max = Math.max(
    1,
    ...years.map((y) => tracks.reduce((s, t) => s + (byYearTrack?.[y]?.[t] || 0), 0)),
  );
  return (
    <div>
      <div className="flex items-end gap-6 h-40">
        {years.map((y) => {
          const yearData = byYearTrack?.[y] || {};
          const total = tracks.reduce((s, t) => s + (yearData[t] || 0), 0);
          return (
            <div key={y} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
              <div className="text-xs font-medium text-[#1C352D]">{total}</div>
              <div className="w-full flex flex-col rounded-md overflow-hidden" style={{ height: `${(total / max) * 100}%`, minHeight: total > 0 ? 8 : 2 }}>
                {tracks.map((t) => {
                  const v = yearData[t] || 0;
                  if (!v) return null;
                  const h = total ? (v / total) * 100 : 0;
                  return <div key={t} title={`${t}: ${v}`} style={{ height: `${h}%`, background: trackColor[t] }}/>;
                })}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">{YEAR_LABEL[Number(y)] || `${y}° anno`}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-4 text-[11px] text-stone-500">
        {tracks.map((t) => (
          <div key={t} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: trackColor[t] }}/>
            <span className="capitalize">{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
